"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { MultiSelectDropdown } from "../../ui/multi-select-dropdown";
import {
  Play,
  Download,
  FileText,
  Calculator,
  BarChart3,
  Loader2,
  MessageSquare,
  Save,
  ChevronDown,
} from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { 
  getProcedureFileUsage, 
  updateProcedureFileUsage,
  type FileUsageEntry
} from "@/utils/fileUsageReference";
import { formatNumber } from "@/utils/formatting";
import {
  PayrollAnalyticalThresholdChart,
  buildPfThresholdChartData,
  buildSalaryThresholdChartData,
  extractPlainInferenceText,
  payrollInferenceTextPayload,
} from "./PayrollAnalyticalThresholdChart";

interface SalaryAnalyticalProps {
  onBack?: () => void;
}

const RISK_ASSESSMENT_OPTIONS = [
  { value: "Lower", label: "Lower" },
  { value: "Higher", label: "Higher" },
  { value: "Significant", label: "Significant" },
];

const CONTROL_RELIANCE_OPTIONS = [
  { value: "Relying on controls", label: "Relying on controls" },
  { value: "Not relying on controls", label: "Not relying on controls" },
];

/** Persisted in Execution_EBE_Responses.json substantiveProcedures */
const EXCLUDED_LEDGER_REMARKS_ID = "SALARY-ANALYTICAL-EXCLUDED-LEDGERS";
const SALARY_ANALYTICAL_PF_INFERENCE_ID = "SALARY-ANALYTICAL-PF-INFERENCE";
const SALARY_ANALYTICAL_SALARY_INFERENCE_ID = "SALARY-ANALYTICAL-SALARY-INFERENCE";
const SALARY_ANALYTICAL_LEGACY_INFERENCE_ID = "SALARY-ANALYTICAL-INFERENCE";

const MONTH_OPTIONS = [
  { value: "1", label: "April" },
  { value: "2", label: "May" },
  { value: "3", label: "June" },
  { value: "4", label: "July" },
  { value: "5", label: "August" },
  { value: "6", label: "September" },
  { value: "7", label: "October" },
  { value: "8", label: "November" },
  { value: "9", label: "December" },
  { value: "10", label: "January" },
  { value: "11", label: "February" },
  { value: "12", label: "March" },
];

const METRICS_TABLE_VARIANTS = {
  pf: {
    header: "bg-gradient-to-r from-violet-600/20 via-violet-500/10 to-transparent",
    iconWrap: "bg-violet-500/15 text-violet-300 ring-1 ring-violet-500/25",
  },
  salary: {
    header: "bg-gradient-to-r from-sky-600/20 via-sky-500/10 to-transparent",
    iconWrap: "bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/25",
  },
} as const;

const isBoldMetricRow = (key: string) => {
  const normalized = key.replace(/_/g, " ").trim().toLowerCase();
  return (
    normalized === "threshold" ||
    normalized === "within threshold" ||
    normalized.includes("expectation") ||
    normalized.includes("expected") ||
    normalized.includes("difference")
  );
};

const shouldHideMetricEntry = (key: string, value: unknown, data: Record<string, unknown>) => {
  const normalizedKey = key.replace(/_/g, " ").trim().toLowerCase();
  if (normalizedKey === "percentage" && (value === "" || value === null || value === undefined)) {
    return true;
  }
  if (normalizedKey === "final threshold" && "Threshold" in data) {
    return true;
  }
  return false;
};

const orderMetricEntries = (entries: [string, unknown][]) => {
  const thresholdIdx = entries.findIndex(
    ([key]) => key.replace(/_/g, " ").trim().toLowerCase() === "threshold",
  );
  const withinIdx = entries.findIndex(
    ([key]) => key.replace(/_/g, " ").trim().toLowerCase() === "within threshold",
  );
  if (thresholdIdx === -1 || withinIdx === -1 || thresholdIdx < withinIdx) {
    return entries;
  }
  const next = [...entries];
  const [thresholdEntry] = next.splice(thresholdIdx, 1);
  const insertAt = next.findIndex(
    ([key]) => key.replace(/_/g, " ").trim().toLowerCase() === "within threshold",
  );
  if (insertAt === -1) {
    next.push(thresholdEntry);
  } else {
    next.splice(insertAt, 0, thresholdEntry);
  }
  return next;
};

function MetricsResultsTable({
  title,
  icon: Icon,
  variant,
  data,
  isWithinThresholdMetricKey,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: keyof typeof METRICS_TABLE_VARIANTS;
  data: Record<string, unknown>;
  isWithinThresholdMetricKey: (key: string) => boolean;
}) {
  const styles = METRICS_TABLE_VARIANTS[variant];
  const entries = orderMetricEntries(
    Object.entries(data).filter(([key, value]) => !shouldHideMetricEntry(key, value, data)),
  );

  const renderThresholdValue = (value: unknown, bold: boolean) => {
    const normalized = typeof value === "string" ? value.trim().toLowerCase() : String(value).trim().toLowerCase();
    if (normalized === "yes") {
      return (
        <span
          className={`inline-flex items-center rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-sm text-emerald-300 ring-1 ring-emerald-500/30 ${bold ? "font-bold" : "font-semibold"}`}
        >
          {String(value)}
        </span>
      );
    }
    if (normalized === "no") {
      return (
        <span
          className={`inline-flex items-center rounded-full bg-rose-500/15 px-2.5 py-0.5 text-sm text-rose-300 ring-1 ring-rose-500/30 ${bold ? "font-bold" : "font-semibold"}`}
        >
          {String(value)}
        </span>
      );
    }
    return <span className={bold ? "font-bold text-white" : "font-medium text-white"}>{String(value)}</span>;
  };

  return (
    <div className="overflow-hidden rounded-md border border-white/10 bg-gradient-to-b from-white/[0.04] to-black/30 shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
      <div className={`flex items-center gap-3 border-b border-white/10 px-5 py-3.5 ${styles.header}`}>
        <div className={`flex h-9 w-9 items-center justify-center rounded-md ${styles.iconWrap}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-white">{title}</h3>
          <p className="text-sm text-white/45">Analytical procedure output</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full table-auto text-base">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.03]">
              <th className="px-5 py-3 text-left text-sm font-semibold uppercase tracking-wider text-white/55">
                Particulars
              </th>
              <th className="w-[1%] whitespace-nowrap border-l border-white/15 pl-4 pr-5 py-3 text-right text-sm font-semibold uppercase tracking-wider text-white/55">
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, value], index) => {
              const isThreshold = isWithinThresholdMetricKey(key);
              const isBoldRow = isBoldMetricRow(key);
              const displayKey = key.replace(/_/g, " ");
              return (
                <tr
                  key={key}
                  className={`group border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.05] ${
                    index % 2 === 1 ? "bg-white/[0.02]" : ""
                  } ${isBoldRow ? "font-bold" : ""}`}
                >
                  <td className={`px-5 py-3.5 ${isBoldRow ? "font-bold text-white" : "text-white/85"}`}>
                    {displayKey}
                  </td>
                  <td className={`w-[1%] whitespace-nowrap border-l border-white/15 pl-4 pr-5 py-3.5 text-right tabular-nums ${isBoldRow ? "font-bold" : ""}`}>
                    {isThreshold ? (
                      renderThresholdValue(value, isBoldRow)
                    ) : (
                      <span className={isBoldRow ? "font-bold text-white" : "font-medium text-white/95"}>
                        {typeof value === "number" ? formatNumber(value) : String(value)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SalaryAnalytical({ onBack }: SalaryAnalyticalProps) {
  const { isArchivalMode } = useArchivalMode();
  // Salary Calculations state
  const [riskAssessment, setRiskAssessment] = useState<string>("");
  const [controlReliance, setControlReliance] = useState<string>("");
  const [performanceMatrix, setPerformanceMatrix] = useState<string>("");
  const [performanceMaterialityLoadError, setPerformanceMaterialityLoadError] = useState<string | null>(null);
  const [weightedAvgHeadcountPY, setWeightedAvgHeadcountPY] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");

  // PF Analytics state
  const [pfLedgerOptions, setPfLedgerOptions] = useState<string[]>([]);
  const [salaryLedgerOptions, setSalaryLedgerOptions] = useState<string[]>([]);
  const [selectedPfLedgers, setSelectedPfLedgers] = useState<string[]>([]);
  const [selectedSalaryLedgers, setSelectedSalaryLedgers] = useState<string[]>([]);
  const [selectedExcludeLedgers, setSelectedExcludeLedgers] = useState<string[]>([]);
  const [pfFixedMonthlyOption, setPfFixedMonthlyOption] = useState<string>("");
  const [pfHeadcount, setPfHeadcount] = useState<string>("");
  const [percentage, setPercentage] = useState<string>("");

  // UI state
  const [isLoadingPfLedgers, setIsLoadingPfLedgers] = useState(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [pfInferenceText, setPfInferenceText] = useState<string>("");
  const [salaryInferenceText, setSalaryInferenceText] = useState<string>("");
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);
  const [isInputsOpen, setIsInputsOpen] = useState(false);
  const [excludedLedgerRemarks, setExcludedLedgerRemarks] = useState<Record<string, string>>({});
  const [excludedRemarksSaveStatus, setExcludedRemarksSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [excludedRemarksSaveError, setExcludedRemarksSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `SALARY_ANALYTICAL_${normalizedSection}`;
  };
  const [isLoadingSalaryLedgers, setIsLoadingSalaryLedgers] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [outputData, setOutputData] = useState<any>(null);
  const [isLoadingOutput, setIsLoadingOutput] = useState(false);

  // Determine Salary note_line_id based on FRF returned by get_frf.py: AS → 30152, Ind AS → 30170
  const getSalaryNoteLineId = async (): Promise<number> => {
    try {
      const frfResult = await (window as any).sharePointAPI?.getFrf?.();
      const frf = frfResult?.success ? frfResult.frf : null;

      if (frf === "Accounting Standards (AS)") {
        return 30152;
      }
      if (frf === "Indian Accounting Standards (Ind AS)") {
        return 30170;
      }
    } catch (error) {
      console.warn("Failed to determine FRF for salary note_line_id, falling back to default 30152", error);
    }

    // Fallback to original default
    return 30152;
  };

  // Determine PF note_line_id based on FRF: AS → 30153, Ind AS → 30171 (for "Select the ledger accounts comprising of provident fund..." dropdown)
  const getPfNoteLineId = async (): Promise<number> => {
    try {
      const frfResult = await (window as any).sharePointAPI?.getFrf?.();
      const frf = frfResult?.success ? frfResult.frf : null;

      if (frf === "Accounting Standards (AS)") {
        return 30153;
      }
      if (frf === "Indian Accounting Standards (Ind AS)") {
        return 30171;
      }
    } catch (error) {
      console.warn("Failed to determine FRF for PF note_line_id, falling back to default 30153", error);
    }

    return 30153;
  };

  // Load PF Ledger Options for "Select the ledger accounts comprising of provident fund..." — note_line_id 30153 (AS) or 30171 (Ind AS) based on FRF
  const loadPfLedgerOptions = async () => {
    setIsLoadingPfLedgers(true);
    try {
      if (window.sharePointAPI?.loadPfLedgers) {
        const noteLineId = await getPfNoteLineId();
        const result = await window.sharePointAPI.loadPfLedgers(noteLineId);
        if (result.success && result.data && Array.isArray(result.data) && result.data.length > 0) {
          const ledgerNames = result.data
            .map((item: any) => item.ledger_name)
            .filter((name: string | undefined): name is string => !!name && name.trim().length > 0);
          const uniqueLedgers = Array.from(new Set(ledgerNames)).sort();
          if (uniqueLedgers.length > 0) {
            setPfLedgerOptions(uniqueLedgers);
            console.log("Loaded PF ledgers from SharePoint:", uniqueLedgers.length);
          } else {
            console.warn("No valid ledger names found in SharePoint data");
            setPfLedgerOptions([]);
          }
        } else {
          console.error("Failed to load PF ledgers:", result?.error || "No data returned");
          setPfLedgerOptions([]);
        }
      } else {
        console.error("SharePoint API loadPfLedgers not available");
        setPfLedgerOptions([]);
      }
    } catch (error) {
      console.error("Error loading PF ledger options:", error);
      setPfLedgerOptions([]);
    } finally {
      setIsLoadingPfLedgers(false);
    }
  };

  // Load Salary Ledger Options (note_line_id determined via get_frf.py)
  const loadSalaryLedgerOptions = async () => {
    setIsLoadingSalaryLedgers(true);
    try {
      if (window.sharePointAPI?.loadPfLedgers) {
        console.log("Calling loadPfLedgers API for salary ledgers with FRF-based note_line_id...");

        const noteLineId = await getSalaryNoteLineId();
        const result = await window.sharePointAPI.loadPfLedgers(noteLineId);
        console.log("loadPfLedgers result for salary:", { success: result?.success, dataLength: result?.data?.length, error: result?.error });

        if (result.success && result.data && Array.isArray(result.data) && result.data.length > 0) {
          const ledgerNames = result.data
            .map((item: any) => item.ledger_name)
            .filter((name: string | undefined): name is string => !!name && name.trim().length > 0);
          const uniqueLedgers = Array.from(new Set(ledgerNames)).sort();
          if (uniqueLedgers.length > 0) {
            setSalaryLedgerOptions(uniqueLedgers);
            console.log(` Loaded ${uniqueLedgers.length} Salary ledgers from SharePoint:`, uniqueLedgers.slice(0, 5));
          } else {
            console.warn(" No valid ledger names found in SharePoint data");
            setSalaryLedgerOptions([]);
          }
        } else {
          console.error(" Failed to load salary ledgers:", result?.error || "No data returned", result);
          setSalaryLedgerOptions([]);
        }
      } else {
        console.error(" SharePoint API loadPfLedgers not available");
        setSalaryLedgerOptions([]);
      }
    } catch (error) {
      console.error(" Error loading salary ledger options:", error);
      setSalaryLedgerOptions([]);
    } finally {
      setIsLoadingSalaryLedgers(false);
    }
  };


  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsage("Salary Analytical");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  const runAnalytics = async () => {
    setProcessingStatus("running");

    try {
      // Save selected PF and Salary ledger account names to file usage reference
      const entries: FileUsageEntry[] = [];
      if (selectedPfLedgers.length > 0) {
        entries.push({
          "Select the ledger accounts comprising of provident fund for substantive analytical procedures on provident fund.": selectedPfLedgers.join(", ")
        });
      }
      if (selectedSalaryLedgers.length > 0) {
        entries.push({
          "Select the ledger accounts comprising balances such as basic salary, etc. on which the substantive analytical procedures are to be performed.": selectedSalaryLedgers.join(", ")
        });
      }
      if (pfFixedMonthlyOption) {
        entries.push({
          "Is the entity opting for the INR 1,800/- per month PF payment option?": pfFixedMonthlyOption === "yes" ? "Yes" : "No",
        });
      }
      if (pfFixedMonthlyOption === "yes" && pfHeadcount) {
        entries.push({
          "Headcount (annual weighted average for INR 1,800 PF option)": pfHeadcount,
        });
      }
      if (pfFixedMonthlyOption === "no" && percentage) {
        entries.push({
          "Mention the provident fund percentage to be used for the analytical procedure": percentage,
        });
      }
      if (entries.length > 0) {
        await updateProcedureFileUsage("Salary Analytical", entries);
        await loadFileUsageReference();
      }

      if (window.payroll?.run) {
        // Prepare inputs for the Python script
        // i_input and ii_input are now ledger names (strings), not indices
        const inputs = {
          risk_assessment: riskAssessment,
          control_reliance: controlReliance,
          per_mat: parseFloat(performanceMatrix) || 0,
          weighted_avg_headcount_py: parseFloat(weightedAvgHeadcountPY) || 0,
          pf_fixed_monthly_option: pfFixedMonthlyOption === "yes",
          pf_headcount: pfFixedMonthlyOption === "yes" ? parseFloat(pfHeadcount) || 0 : undefined,
          percentage: pfFixedMonthlyOption === "yes" ? "" : parseFloat(percentage) || 0,
          i_input: selectedPfLedgers, // Ledger names for PF accounts
          ii_input: selectedSalaryLedgers, // Ledger names for salary accounts
          exclude_input: selectedExcludeLedgers, // Ledger names to exclude
          month_no: parseInt(selectedMonth) || 12
        };

        const res = await window.payroll.run("execute_salary_analytical", {
          inputFiles: [],
          options: inputs,
        });

        if (res.ok) {
          setProcessingStatus("completed");
          await loadOutput();
        } else {
          setProcessingStatus('error');
        }
      } else {
        setProcessingStatus('error');
      }
    } catch (error) {
      console.error("Error running analytics:", error);
      setProcessingStatus('error');
    }
  };

  const isWithinThresholdMetricKey = (key: string) =>
    key.replace(/_/g, " ").trim().toLowerCase() === "within threshold";

  const loadOutput = async () => {
    setIsLoadingOutput(true);
    try {
      if (window.sharePointAPI?.downloadPfSalaryJson) {
        const result = await window.sharePointAPI.downloadPfSalaryJson();
        if (result.success && result.data) {
          setOutputData(result.data);
          const rows = result.data?.Salary?.Excluded_Ledgers_Detail?.Rows as
            | Array<{ Ledger_Name?: string; Remarks?: string }>
            | undefined;
          if (rows?.length) {
            setExcludedLedgerRemarks((prev) => {
              const remarksFromJson: Record<string, string> = { ...prev };
              for (const row of rows) {
                if (!row.Ledger_Name) continue;
                const fromRow = typeof row.Remarks === "string" ? row.Remarks : "";
                // Prefer PFSal remarks; keep prior/EBE value when row remark is empty
                remarksFromJson[row.Ledger_Name] =
                  fromRow.trim().length > 0 ? fromRow : (prev[row.Ledger_Name] ?? "");
              }
              return remarksFromJson;
            });
          }
        } else {
          alert("Failed to load output: " + (result.error || "Unknown error"));
        }
      } else {
        throw new Error("SharePoint API not available");
      }
    } catch (error) {
      console.error("Error loading output:", error);
      alert("Failed to load output: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsLoadingOutput(false);
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "the Audit Client");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("the Audit Client");
      }
    };
    loadCompanyName();
  }, []);

  // Performance materiality from FY-root EngagementDetails.json (SharePoint), same source as getReportingDate
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPerformanceMaterialityLoadError(null);
      try {
        const res = await window.sharePointAPI?.getReportingDate?.();
        if (cancelled) return;
        if (res && res.success === false) {
          setPerformanceMaterialityLoadError(
            res.error ? String(res.error) : "Could not load EngagementDetails.json for performance materiality.",
          );
          return;
        }
        const pm = res?.performance_materiality;
        if (typeof pm === "number" && Number.isFinite(pm) && pm > 0) {
          setPerformanceMatrix(String(pm));
        }
      } catch {
        if (!cancelled) {
          setPerformanceMaterialityLoadError(
            "Failed to load performance materiality from EngagementDetails.json.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load inference question + legacy excluded remarks from Execution_EBE_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_EBE_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const sp = result.data.substantiveProcedures;
          setPfInferenceText(extractPlainInferenceText(sp[SALARY_ANALYTICAL_PF_INFERENCE_ID]?.textResponse));
          const salaryFromNew = extractPlainInferenceText(sp[SALARY_ANALYTICAL_SALARY_INFERENCE_ID]?.textResponse);
          const salaryFromLegacy = extractPlainInferenceText(sp[SALARY_ANALYTICAL_LEGACY_INFERENCE_ID]?.textResponse);
          setSalaryInferenceText(salaryFromNew || salaryFromLegacy);
          // Older archives stored excluded-ledger remarks here; prefer PFSal Rows when present (set in loadOutput).
          const excludedSaved = sp[EXCLUDED_LEDGER_REMARKS_ID]?.remarksByLedger;
          if (excludedSaved && typeof excludedSaved === "object" && !Array.isArray(excludedSaved)) {
            setExcludedLedgerRemarks((prev) => {
              const hasExisting = Object.values(prev).some((v) => String(v || "").trim().length > 0);
              if (hasExisting) return prev;
              return excludedSaved as Record<string, string>;
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  // Load output and ledger options automatically when component mounts
  useEffect(() => {
    loadOutput();
    loadPfLedgerOptions();
    loadSalaryLedgerOptions();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canRunAnalytics = () => {
    const percentageValid = pfFixedMonthlyOption === "yes" || !!percentage;
    const headcountValid = pfFixedMonthlyOption !== "yes" || !!pfHeadcount;
    return !!(
      riskAssessment &&
      controlReliance &&
      performanceMatrix &&
      weightedAvgHeadcountPY &&
      selectedPfLedgers.length > 0 &&
      selectedSalaryLedgers.length > 0 &&
      pfFixedMonthlyOption &&
      percentageValid &&
      headcountValid
    );
  };

  // Update control reliance options based on risk assessment
  const getControlRelianceOptions = () => {
    if (riskAssessment === "Significant") {
      return CONTROL_RELIANCE_OPTIONS.filter(option => option.value === "Relying on controls");
    }
    return CONTROL_RELIANCE_OPTIONS;
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      const areaPrefix = "EBE";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      const now = new Date().toISOString();
      const inferPayload = (text: string) => payrollInferenceTextPayload(text);
      const legacyCombined = [pfInferenceText, salaryInferenceText]
        .map((s) => s.trim())
        .filter(Boolean)
        .join("\n\n---\n\n");

      existingData.substantiveProcedures[SALARY_ANALYTICAL_PF_INFERENCE_ID] = {
        selectedOption: "",
        textResponse: inferPayload(pfInferenceText),
        documents: [],
        table: null,
        lastUpdated: now,
        companyName,
      };
      existingData.substantiveProcedures[SALARY_ANALYTICAL_SALARY_INFERENCE_ID] = {
        selectedOption: "",
        textResponse: inferPayload(salaryInferenceText),
        documents: [],
        table: null,
        lastUpdated: now,
        companyName,
      };
      existingData.substantiveProcedures[SALARY_ANALYTICAL_LEGACY_INFERENCE_ID] = {
        selectedOption: "",
        textResponse: inferPayload(legacyCombined),
        documents: [],
        table: null,
        lastUpdated: now,
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  const saveExcludedLedgerRemarks = async () => {
    if (!outputData?.Salary?.Excluded_Ledgers_Detail?.Rows?.length) {
      setExcludedRemarksSaveError("No excluded ledger data to save. Load output first.");
      setExcludedRemarksSaveStatus("error");
      return;
    }

    setExcludedRemarksSaveStatus("saving");
    setExcludedRemarksSaveError(null);

    try {
      const fileName = "Execution_EBE_PFSal.json";
      const nextData = JSON.parse(JSON.stringify(outputData)) as Record<string, unknown>;
      const salary = (nextData.Salary ?? {}) as Record<string, unknown>;
      const detail = (salary.Excluded_Ledgers_Detail ?? {}) as Record<string, unknown>;
      const rows = Array.isArray(detail.Rows) ? [...detail.Rows] : [];

      detail.Rows = rows.map((row) => {
        const entry = { ...(row as Record<string, unknown>) };
        const name = typeof entry.Ledger_Name === "string" ? entry.Ledger_Name : "";
        entry.Remarks = name ? (excludedLedgerRemarks[name] ?? "") : "";
        return entry;
      });
      salary.Excluded_Ledgers_Detail = detail;
      nextData.Salary = salary;

      const jsonString = JSON.stringify(nextData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: "Execution EBE PF & Salary Analytical",
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save excluded ledger remarks");
      }

      setOutputData(nextData);
      setExcludedRemarksSaveStatus("saved");
      setTimeout(() => setExcludedRemarksSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to save excluded ledger remarks:", error);
      setExcludedRemarksSaveError(error instanceof Error ? error.message : "Unknown error");
      setExcludedRemarksSaveStatus("error");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Substantive Analytical Procedures</h2>
          <p className="text-gray-400">
            Perform substantive analytical procedures on salary and provident fund expenses
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            ← Back
          </Button>
        )}
      </div>

      <Card
        className="relative z-50 border-white/10 bg-black/30 cursor-pointer"
        onClick={() => setIsInputsOpen((prev) => !prev)}
      >
        <CardHeader>
          <Button
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setIsInputsOpen((prev) => !prev);
            }}
            className="w-full justify-between text-white hover:bg-white/10"
            data-allow-archival-edit={true}
          >
            <span className="font-semibold">Inputs</span>
            <ChevronDown className={`h-5 w-5 transition-transform ${isInputsOpen ? "rotate-180" : "rotate-0"}`} />
          </Button>
          <CardDescription className="text-white/55">
            Expand to configure PF analytics, salary calculations, and run analytics parameters.
          </CardDescription>
        </CardHeader>
      </Card>

      {isInputsOpen && (
      <div className="rounded-xl border border-white/10 bg-black/30 p-3 sm:p-5 space-y-6">
      {/* PF Analytics Section */}
      <Card className="relative z-40 border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <BarChart3 className="h-5 w-5" />
              PF Analytics
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-review-comment-anchor={getSectionQuestionId("PF Analytics")}
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("PF Analytics"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Provident Fund Ledger Selection */}
          <div className="space-y-2">
            <Label className="text-white">
              Select the ledger accounts comprising of provident fund for substantive analytical procedures on provident fund.
            </Label>
            <MultiSelectDropdown
              options={pfLedgerOptions}
              value={selectedPfLedgers}
              onChange={setSelectedPfLedgers}
              placeholder="Select ledgers..."
              loading={isLoadingPfLedgers}
              showSelectAll={true}
              disabled={isLoadingPfLedgers}
            />
            {isLoadingPfLedgers && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading ledgers...
              </div>
            )}
            {selectedPfLedgers.length > 0 && (
              <p className="text-sm text-green-400">
                {selectedPfLedgers.length} ledger{selectedPfLedgers.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          {/* Basic Salary Ledger Selection */}
          <div className="space-y-2">
            <Label className="text-white">
              Select the ledger accounts comprising balances such as basic salary, etc. on which the substantive analytical procedures are to be performed.
            </Label>
            <MultiSelectDropdown
              options={salaryLedgerOptions}
              value={selectedSalaryLedgers}
              onChange={setSelectedSalaryLedgers}
              placeholder="Select ledgers..."
              loading={isLoadingSalaryLedgers}
              showSelectAll={true}
              disabled={isLoadingSalaryLedgers}
            />
            {isLoadingSalaryLedgers && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading ledgers...
              </div>
            )}
            {selectedSalaryLedgers.length > 0 && (
              <p className="text-sm text-green-400">
                {selectedSalaryLedgers.length} ledger{selectedSalaryLedgers.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          {/* INR 1,800 PF payment option */}
          <div className="space-y-2">
            <Label className="text-white">
              Is the entity opting for the INR 1,800/- per month PF payment option?
            </Label>
            <Select
              value={pfFixedMonthlyOption}
              onValueChange={(value) => {
                setPfFixedMonthlyOption(value);
                if (value === "yes") {
                  setPercentage("");
                } else {
                  setPfHeadcount("");
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Yes or No" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white">
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Headcount — only when using fixed INR 1,800/month PF option */}
          {pfFixedMonthlyOption === "yes" && (
          <div className="space-y-2">
            <Label className="text-white">Headcount</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="Enter annual weighted average headcount"
              value={pfHeadcount}
              onChange={(e) => setPfHeadcount(e.target.value)}
              className="border-white/10 bg-black/40 text-white"
            />
          </div>
          )}

          {/* Percentage Input — only when not using fixed INR 1,800/month PF option */}
          {pfFixedMonthlyOption === "no" && (
          <div className="space-y-2">
            <Label className="text-white">Mention the provident fund percentage to be used for the analytical procedure</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="Enter increment percentage"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              className="border-white/10 bg-black/40 text-white"
            />
          </div>
          )}
        </CardContent>
      </Card>

      {/* Salary Calculations Section */}
      <Card className="relative z-30 border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Calculator className="h-5 w-5" />
              Salary Calculations
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-review-comment-anchor={getSectionQuestionId("Salary Calculations")}
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Salary Calculations"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Assessment of risk</Label>
              <Select value={riskAssessment} onValueChange={setRiskAssessment}>
                <SelectTrigger>
                  <SelectValue placeholder="Select risk assessment" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {RISK_ASSESSMENT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Control reliance approach</Label>
              <Select
                value={controlReliance}
                onValueChange={setControlReliance}
                disabled={!riskAssessment}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select control reliance" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {getControlRelianceOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Performance Materiality</Label>
              <Input
                type="number"
                placeholder="Enter performance materiality value"
                value={performanceMatrix}
                onChange={(e) => setPerformanceMatrix(e.target.value)}
                className="border-white/10 bg-black/40 text-white"
              />
              {performanceMaterialityLoadError ? (
                <p className="text-sm text-amber-400">{performanceMaterialityLoadError}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label className="text-white">Weighted Avg Headcount Previous Year</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Enter weighted average headcount"
                value={weightedAvgHeadcountPY}
                onChange={(e) => setWeightedAvgHeadcountPY(e.target.value)}
                className="border-white/10 bg-black/40 text-white"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Month</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger>
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white">
                {MONTH_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Exclude Ledgers Selection */}
          <div className="space-y-2">
            <Label className="text-white">
              Select ledgers to exclude from salary substantive analytical procedures (optional)
            </Label>
            <MultiSelectDropdown
              options={salaryLedgerOptions}
              value={selectedExcludeLedgers}
              onChange={setSelectedExcludeLedgers}
              placeholder="Select exclude ledgers..."
              loading={isLoadingSalaryLedgers}
              showSelectAll={true}
              disabled={isLoadingSalaryLedgers}
            />
            {selectedExcludeLedgers.length > 0 && (
              <p className="text-sm text-red-400">
                {selectedExcludeLedgers.length} ledger{selectedExcludeLedgers.length !== 1 ? 's' : ''} excluded
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Run Analytics Section */}
      <Card className="relative z-20 border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <Play className="h-5 w-5" />
            Run Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Execute salary analytical procedures with the configured parameters
              </p>
              {!canRunAnalytics() && (
                <p className="mt-1 text-xs text-red-400">
                  Please fill in all required fields and select ledgers
                </p>
              )}
            </div>
            <Button
              onClick={runAnalytics}
              disabled={isArchivalMode || !canRunAnalytics() || processingStatus === "running"}
              className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
            >
              {processingStatus === "running" ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Running Analytics...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run Analytics
                </>
              )}
            </Button>
          </div>

          {processingStatus === "error" && (
            <div className="rounded border border-red-500/20 bg-red-500/10 p-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-red-500" />
                <div>
                  <h4 className="font-medium text-red-300">Error</h4>
                  <p className="text-sm text-red-200">
                    An error occurred while running the analytics. Please try again.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
      )}

      {/* Load Output Section */}
      <Card className="relative z-10 border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <FileText className="h-5 w-5" />
            Load Output
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Load and display the results from the latest analytics run
              </p>
            </div>
            <Button
              onClick={loadOutput}
              disabled={isLoadingOutput}
              className="disabled:opacity-50"
              data-allow-archival-edit={true}
            >
              {isLoadingOutput ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Load Output
                </>
              )}
            </Button>
          </div>

          {outputData && (
            <div className="space-y-6">
              {/* PF Analytics Table */}
              {outputData.PF?.PF_Analytics && (
                <MetricsResultsTable
                  title="PF Analytics"
                  icon={BarChart3}
                  variant="pf"
                  data={outputData.PF.PF_Analytics as Record<string, unknown>}
                  isWithinThresholdMetricKey={isWithinThresholdMetricKey}
                />
              )}

              {outputData.PF?.PF_Analytics &&
                (() => {
                  const chartData = buildPfThresholdChartData(
                    outputData.PF.PF_Analytics as Record<string, unknown>
                  );
                  if (!chartData) return null;
                  return (
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-white">PF threshold analysis</h3>
                      <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
                        <PayrollAnalyticalThresholdChart data={chartData} />
                        <div className="flex min-h-[18rem] flex-col gap-2">
                          <Label className="text-white">Inference / conclusion (PF)</Label>
                          <Textarea
                            value={pfInferenceText}
                            onChange={(e) => setPfInferenceText(e.target.value)}
                            disabled={isArchivalMode}
                            className="min-h-[18rem] flex-1 border-white/10 bg-black/40 text-white placeholder:text-white/40"
                            placeholder="Document inference and conclusion for PF analytical."
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}

              {/* Salary Calculations Table */}
              {outputData.Salary?.Salary_Calculations && (
                <MetricsResultsTable
                  title="Salary Calculations"
                  icon={Calculator}
                  variant="salary"
                  data={outputData.Salary.Salary_Calculations as Record<string, unknown>}
                  isWithinThresholdMetricKey={isWithinThresholdMetricKey}
                />
              )}

              {outputData.Salary?.Salary_Calculations &&
                (() => {
                  const chartData = buildSalaryThresholdChartData(
                    outputData.Salary.Salary_Calculations as Record<string, unknown>
                  );
                  if (!chartData) return null;
                  return (
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-white">Salary threshold analysis</h3>
                      <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
                        <PayrollAnalyticalThresholdChart data={chartData} />
                        <div className="flex min-h-[18rem] flex-col gap-2">
                          <Label className="text-white">Inference / conclusion (salary)</Label>
                          <Textarea
                            value={salaryInferenceText}
                            onChange={(e) => setSalaryInferenceText(e.target.value)}
                            disabled={isArchivalMode}
                            className="min-h-[18rem] flex-1 border-white/10 bg-black/40 text-white placeholder:text-white/40"
                            placeholder="Document inference and conclusion for salary analytical."
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}

              {outputData.Salary?.Excluded_Ledgers_Detail?.Rows?.length ? (
                <div className="space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white">Excluded salary ledgers</h3>
                      <p className="text-sm text-gray-400">
                        Note line:{" "}
                        <span className="text-white/90">
                          {outputData.Salary.Excluded_Ledgers_Detail.Note_Line || "Salaries and Wages"}
                        </span>
                        . CY total for this note line:{" "}
                        <span className="font-medium text-white">
                          {formatNumber(outputData.Salary.Excluded_Ledgers_Detail.Note_Line_CY_Total ?? 0)}
                        </span>
                        . One row per ledger selected to exclude; percentage is that ledger closing balance as a share of
                        the note line total.
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {excludedRemarksSaveStatus === "saved" && (
                        <span className="text-sm text-green-400">Remarks saved</span>
                      )}
                      {excludedRemarksSaveStatus === "error" && excludedRemarksSaveError && (
                        <span className="text-sm text-red-400">{excludedRemarksSaveError}</span>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={isArchivalMode || excludedRemarksSaveStatus === "saving"}
                        onClick={saveExcludedLedgerRemarks}
                        className="border-white/20 text-white hover:bg-white/10"
                        title="Save excluded-ledger remarks to Execution_EBE_PFSal.json"
                      >
                        {excludedRemarksSaveStatus === "saving" ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving…
                          </>
                        ) : (
                          <>
                            <Save className="mr-2 h-4 w-4" />
                            Save remarks
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-white/10 text-sm">
                      <thead>
                        <tr className="bg-black/60">
                          <th className="border border-white/10 px-3 py-2 text-left font-semibold text-white">
                            Ledger
                          </th>
                          <th className="border border-white/10 px-3 py-2 text-right font-semibold text-white">
                            Amount
                          </th>
                          <th className="border border-white/10 px-3 py-2 text-right font-semibold text-white">
                            Percentage
                          </th>
                          <th className="min-w-[200px] border border-white/10 px-3 py-2 text-left font-semibold text-white">
                            Remarks
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {outputData.Salary.Excluded_Ledgers_Detail.Rows.map(
                          (
                            row: {
                              Ledger_Name: string;
                              Closing_Amount: number;
                              Pct_of_Note_Line_Total: number;
                            },
                            idx: number
                          ) => {
                            const name = row.Ledger_Name;
                            return (
                              <tr key={`ex-${name}-${idx}`} className="hover:bg-white/5">
                                <td className="border border-white/10 px-3 py-2 align-top text-white">{name}</td>
                                <td className="border border-white/10 px-3 py-2 text-right align-top text-white">
                                  {formatNumber(row.Closing_Amount)}
                                </td>
                                <td className="border border-white/10 px-3 py-2 text-right align-top text-white">
                                  {typeof row.Pct_of_Note_Line_Total === "number" &&
                                  !Number.isNaN(row.Pct_of_Note_Line_Total)
                                    ? `${Math.round(row.Pct_of_Note_Line_Total)}%`
                                    : "—"}
                                </td>
                                <td className="border border-white/10 px-3 py-2 align-top">
                                  <Textarea
                                    value={excludedLedgerRemarks[name] ?? ""}
                                    onChange={(e) =>
                                      setExcludedLedgerRemarks((prev) => ({
                                        ...prev,
                                        [name]: e.target.value,
                                      }))
                                    }
                                    disabled={isArchivalMode}
                                    className="min-h-[72px] border-white/10 bg-black/40 text-white placeholder:text-white/40"
                                    placeholder="Optional"
                                  />
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-white">Save responses</CardTitle>
              <p className="mt-1 text-sm text-gray-400">
                Saves PF and salary inference text (beside the threshold charts) and the combined record
                used for older reports.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {inferenceSaveStatus === "saved" && (
                <span className="text-sm text-green-400">Saved</span>
              )}
              {inferenceSaveStatus === "error" && inferenceSaveError && (
                <span className="text-sm text-red-400">{inferenceSaveError}</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={isArchivalMode || inferenceSaveStatus === "saving" || !companyName}
                size="sm"
                className="bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                {inferenceSaveStatus === "saving" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save all
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("PF Analytics")]: "PF Analytics",
              [getSectionQuestionId("Salary Calculations")]: "Salary Calculations",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}